import { useEffect, useMemo, useRef, useState } from 'react';
import { useQuery } from '@apollo/client';
import { useRouter } from 'next/router';

import { BANNER_MSGS_KEYS, generateApolloClientHeaders } from '../../../modules';
import { phoenixAccountGetWalletPaymentInfo } from '../../../modules/graph';
import { useBannerMessagesStore } from '../../../modules/store/bannerMessagesStore';
import { CustomerPaymentMethod, useProfileStore } from '../../../modules/store/profileStore';
import { SavedCard, useSavedCardsStore } from '../../../modules/store/savedCardsStore';
import { checkPaymentMethods } from '../../ProfileSection/ProfileUtils/profileHelper';
import { PaymentMethod } from '../../ProfileSection/ProfileSectionElement/ProfileSectionElement.types';
import { mapWalletPaymentMethodToSavedCard, WalletPaymentMethod } from '../utils/mapWalletPaymentMethodToSavedCard';

interface PageContextSessionData {
  sessionData?: {
    cacheData?: {
      data?: {
        consumerID?: string;
        email?: string;
      };
    };
  };
}

export interface UseWalletSavedCardsArgs {
  pageContext: PageContextSessionData & Record<string, unknown>;
  isLocal: boolean;
  skip?: boolean;
}

export function useWalletSavedCards({ pageContext, isLocal, skip = false }: UseWalletSavedCardsArgs) {
  const isDataLoaded = useRef(false);
  const paymentMethodsRef = useRef<WalletPaymentMethod[]>([]);
  const iccBannerHandledRef = useRef(false);
  const [hasUxlError, setHasUxlError] = useState(false);
  const router = useRouter();
  const routerQueryRef = useRef(router?.query);
  routerQueryRef.current = router?.query;

  const sessionData = pageContext?.sessionData?.cacheData?.data;
  const customerId = sessionData?.consumerID;

  const shouldSkip = skip || !customerId;

  const handleIccBanner = (paymentMethods: WalletPaymentMethod[]) => {
    if (iccBannerHandledRef.current) {
      return;
    }

    const isIccQuery = routerQueryRef.current?.['ic'] === 'true';
    const currentIsIcc = sessionStorage.getItem('isIcc') === 'true' || isIccQuery;
    if (!currentIsIcc) {
      return;
    }

    if (isIccQuery) {
      sessionStorage.setItem('isIcc', 'true');
    }

    const paymentStatus = checkPaymentMethods(paymentMethods as PaymentMethod[]);
    if (paymentStatus === 'success') {
      useBannerMessagesStore.getState().setBannerMsgs(BANNER_MSGS_KEYS.ICC_SUCCESS_MSG);
      iccBannerHandledRef.current = true;
    } else if (paymentStatus === 'pending') {
      useBannerMessagesStore.getState().setBannerMsgs(BANNER_MSGS_KEYS.ICC_PENDING_MSG);
      iccBannerHandledRef.current = true;
    }
  };

  useEffect(() => {
    if (sessionStorage.getItem('isIcc') === 'true') {
      return;
    }
    if (router?.query['ic'] === 'true') {
      sessionStorage.setItem('isIcc', 'true');
    }
  }, [router?.query]);

  useEffect(() => {
    const isIccQuery = router?.query['ic'] === 'true';
    if (isIccQuery) {
      sessionStorage.setItem('isIcc', 'true');
    }

    if (isDataLoaded.current) {
      handleIccBanner(paymentMethodsRef.current);
    }
  }, [router?.query]);

  // Clear store before fetching fresh data (skip if in mock mode to preserve seeds)
  const shouldSkipRef = useRef(shouldSkip);
  useEffect(() => {
    if (!shouldSkipRef.current) {
      useSavedCardsStore.getState().setProfileSavedCards(undefined as unknown as SavedCard[]);
    }
  }, []);

  const variables = useMemo(
    () => ({
      customerId,
    }),
    [customerId]
  );

  useQuery(phoenixAccountGetWalletPaymentInfo, {
    variables,
    context: generateApolloClientHeaders(isLocal, pageContext),
    fetchPolicy: 'no-cache',
    skip: shouldSkip || isDataLoaded.current,
    onCompleted: (data: Record<string, unknown>) => {
      const walletData = data?.['customer'] as Record<string, unknown> | undefined;
      const paymentMethods =
        ((walletData?.['wallet'] as Record<string, unknown>)?.['paymentMethods'] as WalletPaymentMethod[]) ?? [];

      paymentMethodsRef.current = paymentMethods;
      useSavedCardsStore.getState().setProfileSavedCards(paymentMethods.map(mapWalletPaymentMethodToSavedCard));

      // The BAU AddCard/UpdateCard modals read the card being edited from
      // profileDetails.paymentMethods, which only ProfileSection populates today. Mirror the raw
      // payment methods here so those modals work from the Wallet tab without being forked.
      const { profileDetails, setProfileDetails } = useProfileStore.getState();
      setProfileDetails({
        ...profileDetails,
        paymentMethods: paymentMethods as CustomerPaymentMethod[],
      });

      handleIccBanner(paymentMethods);

      isDataLoaded.current = true;
      setHasUxlError(false);
    },
    onError: () => {
      isDataLoaded.current = false;
      setHasUxlError(true);
      useSavedCardsStore.getState().setProfileSavedCards([]);
    },
  });

  return { hasUxlError };
}
